/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';
import { GetTeams } from './teams.get';

export const AdminOrganizationGet = {
  id: 'admin-organization.get',
  tag: 'Admin',
  summary: 'Find Organizations',
  description: 'Get organizations data',
  method: 'get',
  flags: { isAuthorized: true, isSuperuser: true },
  route: () => `/administration/organization`,
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.array(
            z.strictObject({
              id: ZodTypes.Id,
              name: ZodTypes.KubernetesIdentifier,
              description: z.string().min(1).nullable().optional(),
              email: z.string().email().nullable().optional(),
              companyName: z.string().min(1),
              vatNumber: z.string().nullable().optional(),
              language: ZodTypes.Language,
              verified: z.boolean(),
              invitations: z
                .array(
                  z.strictObject({
                    id: ZodTypes.Id,
                    user: z.strictObject({
                      id: ZodTypes.Id,
                      authentication: z.discriminatedUnion('type', [
                        z.strictObject({ type: z.literal('local') }),
                        z.strictObject({ type: z.literal('oauth2'), oauth2Id: z.string() }),
                        z.strictObject({ type: z.literal('oidc'), oidcId: z.string() }),
                      ]),
                      email: z.string().email(),
                      firstName: z.string().min(1),
                      middleName: z.string().min(1).nullable().optional(),
                      lastName: z.string().min(1),
                      verified: z.boolean(),
                      phoneNationalNumber: z.string().nullable().optional(),
                      phoneCountryCode: ZodTypes.Country.nullable().optional(),
                      marketable: z.boolean(),
                      disableDynamicAllocation: z.boolean(),
                      ssoId: z.string().nullable().optional(),
                    }),
                  }),
                )
                .optional(),
              registrationInvitations: z.array(z.any()).optional(),
              address: z.strictObject({
                id: ZodTypes.Id,
                line1: z.string().min(1),
                line2: z.string().optional().nullable(),
                line3: z.string().optional().nullable(),
                postalCode: z.string().min(1),
                city: z.string().min(1),
                state: z.string().optional().nullable(),
                countryCode: ZodTypes.Country,
              }),
              users: z
                .array(
                  z.strictObject({
                    id: ZodTypes.Id,
                    role: z.string(),
                    user: z.strictObject({
                      id: ZodTypes.Id,
                      authentication: z.discriminatedUnion('type', [
                        z.strictObject({ type: z.literal('local') }),
                        z.strictObject({ type: z.literal('oauth2'), oauth2Id: z.string() }),
                        z.strictObject({ type: z.literal('oidc'), oidcId: z.string() }),
                      ]),
                      email: z.string().email(),
                      firstName: z.string().min(1),
                      middleName: z.string().min(1).nullable().optional(),
                      lastName: z.string().min(1),
                      verified: z.boolean(),
                      phoneNationalNumber: z.string().nullable().optional(),
                      marketable: z.boolean(),
                      disableDynamicAllocation: z.boolean(),
                      ssoId: z.string().nullable().optional(),
                      phoneCountryCode: ZodTypes.Country.nullable().optional(),
                    }),
                  }),
                )
                .optional(),
              projects: z
                .array(
                  z.strictObject({
                    id: ZodTypes.Id,
                    name: z.string(),
                    description: z.string().nullable().optional(),
                    userId: z.string().nullable().optional(),
                    users: z
                      .array(
                        z.strictObject({
                          id: ZodTypes.Id,
                          organizationUser: z.strictObject({
                            id: ZodTypes.Id,
                            role: z.string(),
                            userId: z.string().nullable().optional(),
                            user: z.strictObject({
                              id: ZodTypes.Id,
                              authentication: z.discriminatedUnion('type', [
                                z.strictObject({ type: z.literal('local') }),
                                z.strictObject({ type: z.literal('oauth2'), oauth2Id: z.string() }),
                                z.strictObject({ type: z.literal('oidc'), oidcId: z.string() }),
                              ]),
                              email: z.string().email(),
                              firstName: z.string().min(1),
                              middleName: z.string().min(1).nullable().optional(),
                              lastName: z.string().min(1),
                              verified: z.boolean(),
                              phoneCountryCode: ZodTypes.Country.nullable().optional(),
                              phoneNationalNumber: z.string().nullable().optional(),
                              marketable: z.boolean(),
                              disableDynamicAllocation: z.boolean(),
                              ssoId: z.string().nullable().optional(),
                            }),
                          }),
                        }),
                      )
                      .optional(),
                    clusters: z
                      .array(
                        z.strictObject({
                          id: ZodTypes.Id,
                          name: z.string(),
                          description: z.string().optional().nullable(),
                          type: ZodTypes.ServiceType,
                        }),
                      )
                      .optional(),
                  }),
                )
                .optional(),
              teams: GetTeams.response['200'].content['application/json'].schema,
            }),
          ),
        },
      },
    },
  },
} as const satisfies Endpoint;
